import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Settings, 
  X, 
  Eye, 
  EyeOff,
  ArrowUp,
  ArrowDown,
  RotateCcw
} from 'lucide-react';
import { COLUMN_DEFINITIONS } from './AssetsTable';

interface ColumnConfigProps {
  visibleColumns: string[];
  onVisibleColumnsChange: (columns: string[]) => void;
  isOpen: boolean;
  onClose: () => void;
}

// Default visible columns for better UX
const DEFAULT_VISIBLE_COLUMNS = [
  'name',
  'type', 
  'asset_category',
  'status',
  'value_assessment',
  'location',
  'review_date',
  'actions'
];

const ColumnConfig: React.FC<ColumnConfigProps> = ({
  visibleColumns,
  onVisibleColumnsChange,
  isOpen,
  onClose,
}) => {
  const [localColumns, setLocalColumns] = useState(visibleColumns);

  const toggleColumn = (columnKey: string) => {
    if (localColumns.includes(columnKey)) {
      setLocalColumns(localColumns.filter(col => col !== columnKey));
    } else {
      setLocalColumns([...localColumns, columnKey]);
    }
  };

  const moveColumn = (columnKey: string, direction: 'up' | 'down') => {
    const currentIndex = localColumns.indexOf(columnKey);
    if (currentIndex === -1) return;

    const newColumns = [...localColumns];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex >= 0 && targetIndex < newColumns.length) {
      [newColumns[currentIndex], newColumns[targetIndex]] = [newColumns[targetIndex], newColumns[currentIndex]];
      setLocalColumns(newColumns);
    }
  };

  const resetToDefault = () => {
    setLocalColumns([...DEFAULT_VISIBLE_COLUMNS]);
  };

  const applyChanges = () => {
    onVisibleColumnsChange(localColumns);
    onClose();
  };

  const cancelChanges = () => {
    setLocalColumns(visibleColumns);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Konfigurace sloupců</span>
              </CardTitle>
              <CardDescription>
                Vyberte sloupce k zobrazení a upravte jejich pořadí
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={cancelChanges}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary */}
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm text-muted-foreground">
              Zobrazeno {localColumns.length} z {Object.keys(COLUMN_DEFINITIONS).length} sloupců
            </span>
            <Button variant="outline" size="sm" onClick={resetToDefault}>
              <RotateCcw className="h-3 w-3 mr-2" />
              Výchozí
            </Button>
          </div>

          {/* Column List */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {Object.entries(COLUMN_DEFINITIONS).map(([key, definition]) => {
              const isVisible = localColumns.includes(key);
              const currentIndex = localColumns.indexOf(key);
              
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isVisible ? 'bg-blue-50 border-blue-200' : 'bg-background border-border'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleColumn(key)}
                      className="h-8 w-8 p-0"
                    >
                      {isVisible ? (
                        <Eye className="h-4 w-4 text-blue-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                    <div>
                      <div className="font-medium">{definition.label}</div>
                      <div className="text-xs text-muted-foreground flex space-x-2">
                        {definition.sortable && <Badge variant="outline" className="text-xs px-1 py-0">Řaditelné</Badge>}
                        {definition.filterable && <Badge variant="outline" className="text-xs px-1 py-0">Filtrovatelné</Badge>}
                      </div>
                    </div>
                  </div>

                  {isVisible && (
                    <div className="flex items-center space-x-1">
                      <span className="text-xs text-muted-foreground min-w-[20px]">
                        {currentIndex + 1}
                      </span>
                      <div className="flex flex-col">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveColumn(key, 'up')}
                          disabled={currentIndex === 0}
                          className="h-4 w-6 p-0"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveColumn(key, 'down')}
                          disabled={currentIndex === localColumns.length - 1}
                          className="h-4 w-6 p-0"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={cancelChanges}>
              Zrušit
            </Button>
            <Button onClick={applyChanges}>
              Použít změny
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ColumnConfig;
